import { clsx } from 'clsx';
import type { PriceDto } from '@sbg/shared/schemas';
import { UNIT_SUFFIX } from '@sbg/shared/pricing';
import { VisuallyHidden } from '@/components/ui/VisuallyHidden';
import { pricesShown } from '@/lib/catalog/storefront';
import { useFormatters, useT } from '@/lib/i18n';
import { DiscountBadge } from './DiscountBadge';

export interface PriceBlockProps {
  price: PriceDto;
  /** `card`: compact, used in listings · `detail`: larger, used on the product page. */
  size?: 'card' | 'detail';
  className?: string;
}

/**
 * The price of a product: the final price with its unit (`₹1,200 / sq ft`), and — when a discount is
 * running — the struck-through original and a `−20%` badge. Products without a price show
 * "Price on request". Amounts arrive as integer paise and are formatted for India (en-IN).
 * The struck-through figure and the badge are labelled for screen readers ("Original price …").
 */
export function PriceBlock({ price, size = 'card', className }: PriceBlockProps) {
  const t = useT();
  const { formatMoney } = useFormatters();

  // D-363: every stone says "Ask for the price" for now; the rest of this component (amount, unit, discount) is kept
  // for when `pricesShown()` is on again.
  if (!pricesShown() || price.unit === 'ON_REQUEST' || price.finalMinor === null) {
    return (
      <p className={clsx('price price--request', `price--${size}`, className)}>
        {t('price.onRequest')}
      </p>
    );
  }

  const suffix = UNIT_SUFFIX[price.unit];
  const discounted =
    price.discount !== null && price.baseMinor !== null && price.baseMinor > price.finalMinor;

  return (
    <div
      className={clsx('price', `price--${size}`, className)}
      data-discounted={discounted || undefined}
    >
      <p className="price__line">
        <VisuallyHidden>{t('price.now')}: </VisuallyHidden>
        <span className="price__final">{formatMoney(price.finalMinor, price.currency)}</span>
        {suffix ? <span className="price__unit">{suffix}</span> : null}
        {discounted && price.discount ? (
          <DiscountBadge percentOff={price.discount.percentOff} />
        ) : null}
      </p>
      {discounted && price.baseMinor !== null ? (
        <p className="price__was">
          <VisuallyHidden>{t('price.was')}: </VisuallyHidden>
          <del>{formatMoney(price.baseMinor, price.currency)}</del>
        </p>
      ) : null}
    </div>
  );
}
